import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiOutlineCheck, HiOutlineChevronDown } from "react-icons/hi2";

export interface AppSelectOption {
  value: string;
  label: string;
  description?: string;
  badge?: string;
}

interface AppSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: AppSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
}

type MenuPos = { top: number; left: number; width: number; maxHeight: number; openUp: boolean };

/**
 * Custom select with a portaled menu so it isn't clipped by modal overflow.
 */
const AppSelect: React.FC<AppSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  className = "",
  searchable = false,
  searchPlaceholder = "Search…",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState<MenuPos | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const selected = options.find((o) => o.value === value);

  const filtered = searchable
    ? options.filter((o) => {
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return (
          o.label.toLowerCase().includes(q) ||
          o.description?.toLowerCase().includes(q) ||
          o.badge?.toLowerCase().includes(q)
        );
      })
    : options;

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    const viewportPad = 12;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
    const spaceAbove = rect.top - viewportPad;
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(160, Math.min(280, openUp ? spaceAbove - gap : spaceBelow - gap));

    setPos({
      top: openUp ? rect.top - gap : rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight,
      openUp,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, filtered.length]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setPos(null);
      return;
    }

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    const onReposition = () => updatePosition();

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  const portalTarget =
    typeof document !== "undefined"
      ? document.getElementById("modal-root") || document.body
      : null;

  const menu =
    open && pos && portalTarget
      ? createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            style={{
              position: "fixed",
              top: pos.openUp ? undefined : pos.top,
              bottom: pos.openUp ? window.innerHeight - pos.top : undefined,
              left: pos.left,
              width: pos.width,
              maxHeight: pos.maxHeight,
              zIndex: 10000,
            }}
            className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden flex flex-col"
          >
            {searchable && (
              <div className="p-2 border-b border-gray-100 dark:border-gray-700 shrink-0">
                <input
                  autoFocus
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30"
                />
              </div>
            )}
            <ul className="overflow-y-auto overscroll-contain py-1 flex-1 min-h-0">
              {filtered.length === 0 ? (
                <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  No options
                </li>
              ) : (
                filtered.map((option) => {
                  const isActive = option.value === value;
                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onClick={() => {
                          onChange(option.value);
                          setOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors
                          ${
                            isActive
                              ? "bg-[#5932EA]/10 text-[#5932EA]"
                              : "text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/70"
                          }`}
                      >
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">{option.label}</span>
                            {option.badge && (
                              <span
                                className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-md ${
                                  isActive
                                    ? "bg-[#5932EA]/15 text-[#5932EA]"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300"
                                }`}
                              >
                                {option.badge}
                              </span>
                            )}
                          </span>
                          {option.description && (
                            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                              {option.description}
                            </span>
                          )}
                        </span>
                        {isActive && <HiOutlineCheck className="w-5 h-5 shrink-0 mt-0.5" />}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          portalTarget
        )
      : null;

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full min-h-[48px] sm:min-h-[42px] px-4 py-3 sm:py-2.5 rounded-xl border text-left flex items-center gap-3 transition-colors
          ${
            open
              ? "border-[#5932EA] ring-2 ring-[#5932EA]/30"
              : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
          }
          bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200
          disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        <span className={`flex-1 min-w-0 truncate text-base sm:text-sm ${selected ? "" : "text-gray-400"}`}>
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              <span className="truncate font-medium">{selected.label}</span>
              {selected.badge && (
                <span className="shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-300">
                  {selected.badge}
                </span>
              )}
            </span>
          ) : (
            placeholder
          )}
        </span>
        <HiOutlineChevronDown
          className={`w-5 h-5 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {menu}
    </div>
  );
};

export default AppSelect;
